import { z } from 'zod'

import { discounTypeSchema } from '../discounTypes'
import {
  appliedShopDiscountSchema,
  appliedShopDiscountsSchema,
} from '../discountShop'
import { idealoVisibilityOverrideByShopSchema } from '../idealo'
import { commerceAttributesSchema } from './commerceAttributes'

export const imageSchema = z.object({
  xs: z.string(),
  sm: z.string(),
  md: z.string(),
  lg: z.string(),
  xl: z.string(),
  xxl: z.string(),
  altTag: z.string().optional(),
  aiGenerated: z.boolean().optional(),
  src: z.enum(['product', 'options', 'combinations']),
})
export type Image = z.infer<typeof imageSchema>

export const combinationSchema = z.object({

  // ---------------------------------------
  // Felder für den Online-Shop

  productId: z.number(),
  key: z.string(),
  sourceKey: z.string().optional(),

  brand: z.number(),
  manufacturer: z.number().optional(),
  categorie: z.number(),
  allCategories: z.array(z.number()),
  productType: z.string().optional(),
  listingGroupKey: z.string().optional(),
  autoListingGroupKey: z.string().optional(),
  listingVariantMode: z.enum(['attribute', 'combination']).optional(),
  listingVariantAttributeId: z.number().optional(),
  crossSellManufacturer: z.string().optional(),
  crossSellSerie: z.string().optional(),
  crossSellPart: z.string().optional(),
  crossSellColor: z.string().optional(),
  shops: z.array(z.number()),
  filter: z.array(z.number()),
  menues: z.array(z.number()).optional(),

  metaTitle: z.string(),
  metaTitleSoundex: z.array(z.string()),
  searchKeywords: z.array(z.string()).optional(),
  searchText: z.string().optional(),
  searchTokens: z.array(z.string()).optional(),
  searchPrefixes: z.array(z.string()).optional(),
  searchSoundex: z.array(z.string()).optional(),
  searchExact: z.array(z.string()).optional(),
  searchTitleTokens: z.array(z.string()).optional(),
  searchTitlePrefixes: z.array(z.string()).optional(),
  searchTitleExact: z.array(z.string()).optional(),
  searchBrandTokens: z.array(z.string()).optional(),
  searchBrandExact: z.array(z.string()).optional(),
  searchProductTypeTokens: z.array(z.string()).optional(),
  searchProductTypePrefixes: z.array(z.string()).optional(),
  searchProductTypeExact: z.array(z.string()).optional(),
  searchKeywordTokens: z.array(z.string()).optional(),
  searchKeywordExact: z.array(z.string()).optional(),
  searchOptionTokens: z.array(z.string()).optional(),
  searchOptionPrefixes: z.array(z.string()).optional(),
  searchOptionExact: z.array(z.string()).optional(),
  searchLowTokens: z.array(z.string()).optional(),
  searchLowPrefixes: z.array(z.string()).optional(),

  ean: z.string().optional(),
  sku: z.string().optional(),
  mpn: z.string().optional(),
  seasonCode: z.string().optional(),
  originCountry: z.string().optional(),
  commerceAttributes: commerceAttributesSchema.optional(),

  price: z.number(),
  idealo_bestpreis: z.number().optional(),
  idealoShopMode: z.enum(['single', 'dual']).optional(),
  idealoShopModeCandidate: z.enum(['single', 'dual']).optional(),
  idealoShopModeStableCount: z.number().optional(),
  idealoVisibilityOverride: idealoVisibilityOverrideByShopSchema.optional(),
  deliverytime: z.number(),

  stock: z.number().optional(),

  online: z.boolean(),

  // ---------------------------------------
  // Rabatt
  discountId: z.number().optional(),
  discountPrice: z.number().optional(),
  discountPriceAfterStock: z.number().optional(),
  stockDiscountLimit: z.number().optional(),
  limitStockDiscountToStock: z.boolean().optional(),
  quantityDiscount: appliedShopDiscountSchema.shape.quantityDiscount,
  discountTitle: z.string().optional(),
  discountType: discounTypeSchema.optional(),
  discountCountdownVariant: appliedShopDiscountSchema.shape.countdownVariant,
  discountShowPercent: z.boolean().optional(),
  discountDateStart: z.date().optional(),
  discountDateEnd: z.date().optional(),
  discountShops: z.array(z.number()).optional(),
  shopDiscounts: appliedShopDiscountsSchema.optional(),

  // ---------------------------------------
  // Felder für die WaWi

  productTitle: z.string(),
  productOptions: z.string().optional(),
  brandTitle: z.string(),
  shippingTypeTitle: z.union([z.literal('Paket'), z.literal('Spedition')]),
  shippingDanger: z.boolean(),
  noteStock: z.string().optional(),
  customsTariffNumber: z.string().optional(),

  priceNetto: z.number(),                 // Ohne MwSt.
  priceAb: z.number().optional(),         // Der Preis der auf der AB erscheint. Abzüglich Produkt-Rabatt aber ohne Hersteller-Rabatt.
  priceEk: z.number().optional(),         // Der eingetragene EK-Preis
  priceDiscountEk: z.number().optional(), // Der EK-Preis abzüglich Produkt- und Hersteller-Rabatt
  discountRule: z.string().optional(),    // Die Produkt-Rabatt-Regel

  image: imageSchema.optional(),

  // ---------------------------------------
  // Felder für Idealo & Co.

  formerPrice:           z.number().optional(),
  paymentCosts_standard: z.number().optional(),
  paymentCosts_paypal:   z.number().optional(),

  deliveryComment:       z.string().optional(),
  deliveryCosts_dhl:     z.number().optional(),
  minimumOrderSurcharge: z.number().optional(),

  descriptionShort:   z.string().optional(),
  descriptionDetails: z.string().optional(),
  url:                z.string().optional(),
  imageUrls:          z.array(z.string()).optional(),

  categoryPath_idealo: z.string().optional(),

  attr_size:     z.string().optional(),
  attr_color:    z.string().optional(),
  attr_gender:   z.string().optional(),
  attr_material: z.string().optional(),

})
export type Combination = z.infer<typeof combinationSchema>
